import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

const useVoiceInput = (onFinalTranscript) => {
    const [isRecording, setIsRecording] = useState(false);
    const [interimTranscript, setInterimTranscript] = useState('');
    const recognitionRef = useRef(null);
    const callbackRef = useRef(onFinalTranscript);
    const wantRecordingRef = useRef(false);
    const restartTimeoutRef = useRef(null);

    // Keep callback ref up to date without recreating recognition
    callbackRef.current = onFinalTranscript;

    const ensureRecognition = useCallback(() => {
        if (recognitionRef.current) return recognitionRef.current;

        if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
            toast.error('Tu navegador no soporta dictado por voz.');
            return null;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'es-ES';

        recognition.onstart = () => {
            setIsRecording(true);
        };

        recognition.onresult = (event) => {
            let interim = '';
            let finalText = '';

            for (let i = event.resultIndex; i < event.results.length; ++i) {
                if (event.results[i].isFinal) {
                    finalText += event.results[i][0].transcript;
                } else {
                    interim += event.results[i][0].transcript;
                }
            }

            if (finalText) {
                callbackRef.current?.(finalText);
                setInterimTranscript('');
            } else {
                setInterimTranscript(interim);
            }
        };

        recognition.onerror = (event) => {
            if (event.error === 'not-allowed') {
                toast.error('Permiso de micrófono denegado. Revisa la configuración del navegador.');
                wantRecordingRef.current = false;
                setIsRecording(false);
                setInterimTranscript('');
            }
            // For 'network', 'no-speech', 'aborted' - don't stop, let onend handle auto-restart
        };

        recognition.onend = () => {
            if (wantRecordingRef.current) {
                // Auto-restart: the user still wants to record but the API disconnected
                clearTimeout(restartTimeoutRef.current);
                restartTimeoutRef.current = setTimeout(() => {
                    if (wantRecordingRef.current && recognitionRef.current) {
                        try {
                            recognitionRef.current.start();
                        } catch (e) {
                            // If restart fails, give up
                            wantRecordingRef.current = false;
                            setIsRecording(false);
                            setInterimTranscript('');
                        }
                    }
                }, 300);
            } else {
                setIsRecording(false);
                setInterimTranscript('');
            }
        };

        recognitionRef.current = recognition;
        return recognition;
    }, []);

    const startRecording = useCallback(() => {
        const recognition = ensureRecognition();
        if (!recognition) return;

        try {
            if (wantRecordingRef.current) {
                // User wants to stop
                wantRecordingRef.current = false;
                clearTimeout(restartTimeoutRef.current);
                recognition.stop();
            } else {
                // User wants to start
                wantRecordingRef.current = true;
                setInterimTranscript('');
                recognition.start();
            }
        } catch (error) {
            console.error("Voice start/stop error:", error);
            wantRecordingRef.current = false;
            setIsRecording(false);
        }
    }, [ensureRecognition]);

    const stopRecording = useCallback(() => {
        wantRecordingRef.current = false;
        clearTimeout(restartTimeoutRef.current);
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                // Already stopped
            }
        }
        setIsRecording(false);
        setInterimTranscript('');
    }, []);

    return { isRecording, interimTranscript, startRecording, stopRecording };
};

export default useVoiceInput;
